import React, { useEffect, useRef, useState } from 'react';

import { MARKETING_EMAIL_CONSENT_VERSION, TOS_VERSION } from '../../constants/legal';
import cognitoAuthService, {
  CognitoRegisterRequest,
  PasswordRejectedError,
} from '../../services/cognitoAuthService';
import { FILLS, FONTS, STROKES, TYPE } from '../../styles/tokens';
import { SignUpDetails, validateSignUpDetails } from '../../utils/authFormValidation';
import MarketingEmailConsent from '../forms/MarketingEmailConsent';
import TermsOfService from '../forms/TermsOfService';
import EmailVerification from './EmailVerification';
import NewPasswordHint from './NewPasswordHint';
import { raceWithTimeout, useEmailCodeVerification } from './useEmailCodeVerification';

export type SignUpStep = 'basic' | 'terms' | 'verification';

export interface SignUpStepNavigation {
  step: SignUpStep;
  stepIndex: number;
  isLoading: boolean;
  tosAccepted: boolean;
  /** Id of the basic-details form, for a submit button rendered outside it. */
  basicFormId: string;
  goBack: () => void;
  goNext: () => void;
}

export interface SignUpFormProps {
  /** Called once the e-mail code is accepted. The password is held in component state only. */
  onVerified: (credentials: { email: string; password: string }) => void;
  renderStepNavigation: (navigation: SignUpStepNavigation) => React.ReactNode;
  /** Rendered above the stepper, e.g. a shortcut that skips the form. */
  renderBeforeStepper?: (step: SignUpStep) => React.ReactNode;
  /**
   * Pop-up layout: sets the step heading below a dialog title and fits the code step to the
   * dialog instead of the viewport. The page keeps the larger heading and the viewport-high step.
   */
  compactStepHeading?: boolean;
  /** Sign-in pop-up only: the code step's primary button renders in the sheet's pinned footer. */
  pinCodeStepPrimaryToSheetFooter?: boolean;
}

const REGISTRATION_TIMEOUT_MS = 30000;

const BASIC_FORM_ID = 'reg-basic-form';

const SIGN_UP_STEPS: { key: SignUpStep; label: string }[] = [
  { key: 'basic', label: 'Personal Details' },
  { key: 'terms', label: 'Terms & Agreement' },
  { key: 'verification', label: 'Email Verification' },
];

const SignUpForm: React.FC<SignUpFormProps> = ({
  onVerified,
  renderStepNavigation,
  renderBeforeStepper,
  compactStepHeading = false,
  pinCodeStepPrimaryToSheetFooter = false,
}) => {
  const [currentStep, setCurrentStep] = useState<SignUpStep>('basic');
  const [formData, setFormData] = useState<SignUpDetails>({
    full_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone_number: '',
  });
  const [tosAccepted, setTosAccepted] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    verificationError,
    verificationLoading,
    verificationSuccess,
    handleVerifyEmail,
    handleResendCode,
  } = useEmailCodeVerification(formData.email, () =>
    onVerified({ email: formData.email, password: formData.password }),
  );

  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const steps = SIGN_UP_STEPS;
  const currentStepIndex = steps.findIndex((step) => step.key === currentStep);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const validateBasicInfo = (): boolean => {
    const detailsError = validateSignUpDetails(formData);
    setError(detailsError ?? '');
    return detailsError === null;
  };

  const validateTerms = (): boolean => {
    if (!tosAccepted) {
      setError('You must accept the Terms of Service to create an account');
      return false;
    }
    setError('');
    return true;
  };

  const handleNext = async () => {
    setError('');

    if (currentStep === 'basic' && !validateBasicInfo()) {
      return;
    }

    if (currentStep === 'terms' && !validateTerms()) {
      return;
    }

    // If we're on the terms step, register with Cognito and move to verification
    if (currentStep === 'terms') {
      await handleCognitoRegistration();
      return;
    }

    if (currentStepIndex < steps.length - 1) {
      setCurrentStep(steps[currentStepIndex + 1].key);
    }
  };

  const handleBack = () => {
    setError('');

    // The account already exists once verification starts, so there is no way back.
    if (currentStep === 'verification') {
      return;
    }

    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1].key);
    }
  };

  const handleCognitoRegistration = async () => {
    if (!validateTerms()) return;

    setIsLoading(true);
    setError('');

    try {
      const registerData: CognitoRegisterRequest = {
        email: formData.email.trim(),
        password: formData.password,
        full_name: formData.full_name.trim(),
        phone_number: formData.phone_number?.trim() || undefined,
        tos_accepted: true,
        tos_version: TOS_VERSION,
        email_marketing_opt_in: marketingOptIn,
        email_marketing_consent_version: MARKETING_EMAIL_CONSENT_VERSION,
      };

      const result = await raceWithTimeout(
        cognitoAuthService.register(registerData),
        REGISTRATION_TIMEOUT_MS,
        'Registration timeout - please try again',
      );

      if (result.requires_verification) {
        setCurrentStep('verification');
      } else {
        // Automatically verified (unlikely with email verification enabled)
        onVerified({ email: formData.email, password: formData.password });
      }
    } catch (caught: unknown) {
      if (caught instanceof PasswordRejectedError) {
        // The typed password stays in state so the guest edits it on the details step.
        setCurrentStep('basic');
      }
      setError(
        caught instanceof Error && caught.message
          ? caught.message
          : 'Registration failed. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'basic':
        return (
          <form
            id={BASIC_FORM_ID}
            onSubmit={(event) => {
              event.preventDefault();
              handleNext();
            }}
          >
            {/* Hidden native submit control: the visible "Next" button lives outside
                this form (shared nav) and is wired via the form= attribute, which
                drives click-to-submit but is not picked up by jsdom's implicit
                (Enter-key) submission. This descendant button is what makes Enter
                advance the step. */}
            <button type="submit" aria-hidden="true" tabIndex={-1} style={{ display: 'none' }} />
            <h3
              style={{
                ...(compactStepHeading
                  ? TYPE.itemName
                  : { fontFamily: FONTS.display, fontSize: '22px', fontWeight: 500 }),
                color: FILLS.textPrimary,
                marginTop: 0,
                marginBottom: '8px',
              }}
            >
              Personal Information
            </h3>
            <p
              style={{
                ...TYPE.secondary,
                color: FILLS.textMuted,
                marginBottom: '24px',
              }}
            >
              Let's start with some basic information about you.
            </p>

            <div className="form-group">
              <label className="form-label" htmlFor="reg-full-name">
                Full Name
              </label>
              <input
                className="form-input"
                id="reg-full-name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                autoFocus
                autoComplete="name"
                aria-invalid={!!error}
                aria-describedby={error ? 'registration-error-message' : undefined}
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="reg-email">
                Email Address
              </label>
              <input
                className="form-input"
                id="reg-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="username"
                aria-invalid={!!error}
                aria-describedby={error ? 'registration-error-message' : undefined}
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="reg-phone">
                Phone Number (Optional)
              </label>
              <input
                className="form-input"
                id="reg-phone"
                name="phone_number"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={formData.phone_number}
                onChange={handleChange}
                aria-invalid={!!error && error.includes('phone number')}
                aria-describedby={
                  error && error.includes('phone number')
                    ? 'registration-error-message'
                    : 'phone-helper'
                }
              />
              <span
                id="phone-helper"
                style={{
                  ...TYPE.finePrint,
                  color: !!error && error.includes('phone number') ? FILLS.error : FILLS.textMuted,
                  marginTop: '4px',
                  display: 'block',
                }}
              >
                {!!error && error.includes('phone number')
                  ? error
                  : 'For booking confirmations and support'}
              </span>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="reg-password">
                Password
              </label>
              <input
                className="form-input"
                id="reg-password"
                name="password"
                type="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                required
                aria-invalid={!!error}
                aria-describedby={error ? 'registration-error-message' : 'password-helper'}
              />
              <NewPasswordHint id="password-helper" password={formData.password} />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="reg-confirm-password">
                Confirm Password
              </label>
              <input
                className="form-input"
                id="reg-confirm-password"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                aria-invalid={!!error}
                aria-describedby={error ? 'registration-error-message' : undefined}
              />
            </div>
          </form>
        );

      case 'terms':
        return (
          <div>
            <TermsOfService
              accepted={tosAccepted}
              onAcceptanceChange={setTosAccepted}
              disabled={isLoading}
            />
            <div
              style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: `1px solid ${STROKES.subtle}`,
              }}
            >
              <MarketingEmailConsent
                id="reg-marketing-email"
                checked={marketingOptIn}
                onChange={setMarketingOptIn}
                disabled={isLoading}
              />
            </div>
          </div>
        );

      case 'verification':
        return (
          <div style={{ textAlign: 'center' }}>
            <EmailVerification
              email={formData.email}
              onVerifyCode={handleVerifyEmail}
              onResendCode={handleResendCode}
              loading={verificationLoading}
              error={verificationError}
              success={verificationSuccess}
              fillViewport={!compactStepHeading}
              pinPrimaryToSheetFooter={pinCodeStepPrimaryToSheetFooter}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {renderBeforeStepper?.(currentStep)}

      {/* Step dots */}
      <div className="stepper">
        {steps.map((step, index) => (
          <div
            key={step.key}
            className={`step-dot ${index === currentStepIndex ? 'active' : ''} ${index < currentStepIndex ? 'done' : ''}`}
            title={step.label}
          />
        ))}
      </div>

      {/* Error alert */}
      {error && (
        <div
          ref={errorRef}
          className="alert alert-error"
          id="registration-error-message"
          role="alert"
          style={{ marginBottom: '24px' }}
        >
          {error}
        </div>
      )}

      {/* Step content */}
      <div aria-busy={isLoading}>{renderStepContent()}</div>

      {renderStepNavigation({
        step: currentStep,
        stepIndex: currentStepIndex,
        isLoading,
        tosAccepted,
        basicFormId: BASIC_FORM_ID,
        goBack: handleBack,
        goNext: handleNext,
      })}
    </>
  );
};

export default SignUpForm;
